export function initCustomCursor(options = {}) {
  const customCursorEnabled = options.customCursor === true;
  const cursorTrailEnabled = options.cursorTrail === true;

  if (!customCursorEnabled && !cursorTrailEnabled) return;

  if (
    "ontouchstart" in window ||
    navigator.maxTouchPoints > 0 ||
    navigator.msMaxTouchPoints > 0
  ) {
    return;
  }

  let cursor = null;

  if (customCursorEnabled) {
    document.documentElement.classList.add("wr-has-custom-cursor");
    document.body.classList.add("wr-has-custom-cursor");

    cursor = document.querySelector(".wr-custom-cursor");
    if (!cursor) {
      cursor = document.createElement("div");
      cursor.className = "wr-custom-cursor";
      document.body.appendChild(cursor);
    }
  }

  let trail = null;

  if (cursorTrailEnabled) {
    trail = document.querySelector(".wr-cursor-trail");
    if (!trail) {
      trail = document.createElement("div");
      trail.className = "wr-cursor-trail";
      document.body.appendChild(trail);
    }
  }

  let lastX = window.innerWidth / 2;
  let lastY = window.innerHeight / 2;
  let trailX = lastX;
  let trailY = lastY;
  let hasPointerPosition = false;
  let trailFrame = 0;
  let trailSurfaceFrame = 0;
  let trailSurfaces = [];
  let trailSurfacesCollected = false;
  let lastTarget = null;
  let isScrollDrag = false;
  const activeCursorValues = new Set(["pointer", "grab", "grabbing", "text", "vertical-text"]);
  const textInputTypes = new Set(["text", "search", "url", "tel", "email", "password", "number"]);
  const trailSuppressionSelector = ".website-navigator-overlay, .website-navigator-shell, [data-website-navigator]";

  const getTargetElement = (target) => {
    if (target instanceof Element) return target;
    if (target?.parentElement instanceof Element) return target.parentElement;
    return null;
  };

  const isTextFieldTarget = (target) => {
    const targetElement = getTargetElement(target);
    if (!targetElement) return false;
    const editable = targetElement.closest("[contenteditable]");
    if (targetElement.isContentEditable || editable?.isContentEditable) return true;
    const textarea = targetElement.closest("textarea");
    if (textarea) return !textarea.disabled && !textarea.readOnly;
    const input = targetElement.closest("input");
    if (!input || input.disabled || input.readOnly) return false;
    const type = String(input.getAttribute("type") || "text").trim().toLowerCase();
    return textInputTypes.has(type);
  };

  const getCaretAtPointer = () => {
    if (!Number.isFinite(lastX) || !Number.isFinite(lastY)) return null;
    if (document.caretPositionFromPoint) {
      const position = document.caretPositionFromPoint(lastX, lastY);
      if (!position) return null;
      return { node: position.offsetNode, offset: position.offset };
    }
    if (document.caretRangeFromPoint) {
      const range = document.caretRangeFromPoint(lastX, lastY);
      if (!range) return null;
      return { node: range.startContainer, offset: range.startOffset };
    }
    return null;
  };

  const isPointInsideRect = (rect) => (
    lastX >= rect.left &&
    lastX <= rect.right &&
    lastY >= rect.top &&
    lastY <= rect.bottom
  );

  const isPointOnTextRange = (textNode, offset) => {
    const text = String(textNode.textContent || "");
    if (!text.trim()) return false;
    const range = document.createRange();
    const positions = [offset, offset - 1]
      .filter((pos, index, list) => (
        pos >= 0 &&
        pos < text.length &&
        list.indexOf(pos) === index
      ));

    for (const pos of positions) {
      range.setStart(textNode, pos);
      range.setEnd(textNode, pos + 1);
      const rects = Array.from(range.getClientRects());
      if (rects.some(isPointInsideRect)) {
        range.detach?.();
        return true;
      }
    }

    range.detach?.();
    return false;
  };

  const isSelectableTextTarget = (target) => {
    const targetElement = getTargetElement(target);
    if (!targetElement) return false;
    if (isTextFieldTarget(targetElement)) return true;
    const caret = getCaretAtPointer();
    const textNode = caret?.node || null;
    if (!textNode || textNode.nodeType !== Node.TEXT_NODE) return false;
    const textElement = textNode.parentElement;
    if (!textElement) return false;
    if (!targetElement.contains(textElement) && !textElement.contains(targetElement)) return false;
    const computed = window.getComputedStyle(textElement);
    return (
      isPointOnTextRange(textNode, caret.offset) &&
      computed.userSelect !== "none" &&
      computed.visibility !== "hidden" &&
      computed.display !== "none"
    );
  };

  const getCursorActivity = (target) => {
    const targetElement = getTargetElement(target);
    if (!targetElement) return { active: false, computed: "", selectableText: false };
    const computed = window.getComputedStyle(targetElement).cursor;
    const selectableText = isSelectableTextTarget(targetElement);
    return {
      active: activeCursorValues.has(computed) || selectableText,
      computed,
      selectableText,
    };
  };

  const isTrailSuppressedTarget = (target) => {
    const targetElement = getTargetElement(target);
    return Boolean(targetElement?.closest?.(trailSuppressionSelector));
  };

  const setTrailSuppressed = (suppressed) => {
    if (!trail) return;
    trail.classList.toggle("is-suppressed", suppressed);
    if (suppressed) hideTrail();
  };

  const getTrailSurfaceName = (element) => {
    const explicit =
      element?.dataset?.cursorTrailSurface ||
      element?.dataset?.cursorTrailTheme;
    if (explicit) return String(explicit).trim().toLowerCase();
    if (
      element?.id === "section-hero" ||
      element?.classList?.contains("section-hero") ||
      element?.classList?.contains("hero-section")
    ) {
      return "hero";
    }
    return "";
  };

  const collectTrailSurfaces = () => {
    if (!trail) return;
    const seen = new Set();
    trailSurfacesCollected = true;
    trailSurfaces = Array.from(
      document.querySelectorAll(
        "[data-cursor-trail-surface], [data-cursor-trail-theme], #section-hero, .section-hero, .hero-section"
      )
    )
      .map((element) => ({
        element,
        name: getTrailSurfaceName(element),
      }))
      .filter(({ element, name }) => {
        if (!element || element === trail || !name || seen.has(element)) return false;
        seen.add(element);
        return true;
      });
  };

  const updateTrailSurface = () => {
    if (!trail) return;
    if (!trailSurfacesCollected) collectTrailSurfaces();
    const surface = trailSurfaces.find(({ element }) => {
      const rect = element.getBoundingClientRect();
      return (
        trailX >= rect.left &&
        trailX <= rect.right &&
        trailY >= rect.top &&
        trailY <= rect.bottom
      );
    });
    if (surface?.name) {
      trail.dataset.cursorTrailSurface = surface.name;
      return;
    }
    trail.removeAttribute("data-cursor-trail-surface");
  };

  const updateTrailState = (target) => {
    const activity = getCursorActivity(target);
    if (trail) trail.classList.toggle("is-active", activity.active);
    return activity;
  };

  const getElementAtPointer = (fallbackTarget = null) => {
    if (!hasPointerPosition || !Number.isFinite(lastX) || !Number.isFinite(lastY)) {
      return getTargetElement(fallbackTarget);
    }
    const currentTarget = document.elementFromPoint(lastX, lastY);
    return getTargetElement(currentTarget) || getTargetElement(fallbackTarget);
  };

  const updateVisibilityAtPointer = (fallbackTarget = null) => {
    updateVisibility(getElementAtPointer(fallbackTarget));
  };

  const scheduleTrailSurfaceUpdate = () => {
    if (trailSurfaceFrame) return;
    trailSurfaceFrame = window.requestAnimationFrame(() => {
      trailSurfaceFrame = 0;
      updateTrailSurface();
      if (hasPointerPosition) updateVisibilityAtPointer(lastTarget);
    });
  };

  const refreshTrailSurfaces = () => {
    trailSurfacesCollected = false;
    collectTrailSurfaces();
    updateTrailSurface();
    if (hasPointerPosition) updateVisibilityAtPointer(lastTarget);
  };

  const renderTrail = () => {
    if (!trail) return;
    trailX += (lastX - trailX) * 0.28;
    trailY += (lastY - trailY) * 0.28;
    trail.style.left = trailX + "px";
    trail.style.top = trailY + "px";
    updateTrailSurface();

    const distanceX = Math.abs(lastX - trailX);
    const distanceY = Math.abs(lastY - trailY);
    if (distanceX > 0.1 || distanceY > 0.1) {
      trailFrame = window.requestAnimationFrame(renderTrail);
      return;
    }
    trailFrame = 0;
  };

  const requestTrailFrame = () => {
    if (!trail || trailFrame) return;
    trailFrame = window.requestAnimationFrame(renderTrail);
  };

  const showTrail = () => {
    if (!trail) return;
    if (trail.classList.contains("is-suppressed")) return;
    updateTrailSurface();
    trail.classList.add("is-visible");
  };

  const hideTrail = () => {
    if (!trail) return;
    trail.classList.remove("is-visible");
  };

  const updateVisibility = (target) => {
    lastTarget = target || lastTarget;
    const activity = updateTrailState(target);
    setTrailSuppressed(isTrailSuppressedTarget(target));

    if (!cursor) return;

    if (!target) {
      cursor.style.opacity = "1";
      return;
    }

    if (isScrollDrag) {
      cursor.style.opacity = "0";
      return;
    }

    const hide =
      activity.selectableText ||
      (activity.computed !== "auto" &&
        activity.computed !== "default" &&
        activity.computed !== "none");

    cursor.style.opacity = hide ? "0" : "1";
  };

  const onTargetChange = (e) => {
    if (Number.isFinite(e.clientX) && Number.isFinite(e.clientY)) {
      lastX = e.clientX;
      lastY = e.clientY;
      hasPointerPosition = true;
    }
    updateVisibilityAtPointer(e.target);
  };

  const onMove = (e) => {
    lastX = e.clientX;
    lastY = e.clientY;

    if (!hasPointerPosition) {
      trailX = lastX;
      trailY = lastY;
      hasPointerPosition = true;
    }

    if (cursor) {
      cursor.style.left = lastX + "px";
      cursor.style.top = lastY + "px";
    }

    updateVisibility(e.target);

    if (trail && !trail.classList.contains("is-suppressed")) {
      showTrail();
      requestTrailFrame();
    }
  };

  const onLeave = () => {
    if (cursor) cursor.style.opacity = "0";
    hideTrail();
  };

  const onEnter = (e) => {
    if (trail && Number.isFinite(e.clientX) && Number.isFinite(e.clientY)) {
      lastX = e.clientX;
      lastY = e.clientY;
      if (!hasPointerPosition) {
        trailX = lastX;
        trailY = lastY;
        hasPointerPosition = true;
      }
    }
    updateVisibility(e.target);
    if (trail && !trail.classList.contains("is-suppressed")) {
      showTrail();
      requestTrailFrame();
    }
  };

  const onMouseDown = (e) => {
    const t = e.target;
    if (t && t.closest && t.closest(".website-navigator-messages, .website-navigator-suggestions")) {
      isScrollDrag = true;
      if (cursor) cursor.style.opacity = "0";
    }
  };

  const onMouseUp = () => {
    if (!isScrollDrag) return;
    isScrollDrag = false;
    updateVisibility(lastTarget);
  };

  const onVisibilityChange = () => {
    if (!document.hidden) return;
    if (cursor) cursor.style.opacity = "0";
    hideTrail();
  };

  window.addEventListener("mousemove", onMove);
  window.addEventListener("mouseleave", onLeave);
  window.addEventListener("mouseenter", onEnter);
  window.addEventListener("mousedown", onMouseDown);
  window.addEventListener("mouseup", onMouseUp);
  window.addEventListener("blur", onLeave);
  window.addEventListener("scroll", scheduleTrailSurfaceUpdate, { passive: true });
  window.addEventListener("resize", refreshTrailSurfaces, { passive: true });
  document.addEventListener("mouseover", onTargetChange);
  document.addEventListener("visibilitychange", onVisibilityChange);
  document.documentElement.addEventListener("mouseleave", onLeave);
  refreshTrailSurfaces();
}
